import { useTranslation } from 'react-i18next'

import { Card } from '../../../components/Card/card'

import { GEOFENCE } from '../../../constants/modal.constants'
import { SingleAssetGeofencesType } from '../../../types/geofence.type'

import { Button, Notification } from '@weirgroup/weir-dls'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { AddGeofence } from '../../geofence/add-geofence'
import { TagType } from '../../../types/bucketTypes'
import { ToastDataType } from '../../../types/toast.type'
import { useDarkMode } from '../../../hooks/use-darkmode'

import '../styles.scss'
import { LoadingSpinner } from '../../loading-spinner'
interface Props {
    geofenceData: SingleAssetGeofencesType[]
    tagData: TagType
    refreshList: () => void;
    setIsAssetUpdated?: Dispatch<SetStateAction<boolean>>
    isGuest: boolean
}

export const GeofenceTab = (props: Props) => {
    const { geofenceData,
        tagData,
        refreshList,
        setIsAssetUpdated,
        isGuest
    } = props
    const { t } = useTranslation()
    const [showAdd, setShowAdd] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [showToast, setShowToast] = useState<boolean>(false)
    const [toastData, setToastData] = useState<any>()
    const [selectedGeofence, setSelectedGeofence] = useState<SingleAssetGeofencesType | null>(null);
    const {darkMode} = useDarkMode();

    // Sort geofences by created_at date (newest first)
    const sortedGeofenceData = [...geofenceData].sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    useEffect(() => {
        if (geofenceData.length > 0) {
            setIsLoading(false);
        } else {
            // Add delay to show spinner before showing "no geofences"
            setTimeout(() => {
                setIsLoading(false);
            }, 3000);
        }
    }, [geofenceData])
    // Removed unnecessary refreshList call on mount to prevent double-fetching and extra renders
    const editGeofence = (geofenceData: SingleAssetGeofencesType) => {
        setShowAdd(true)
        setSelectedGeofence(geofenceData)
    }

    const renderToast = (data: ToastDataType) => {
        setShowToast(true);
        setToastData(data)
        refreshList();
        setShowAdd(false); // Close the add form to show the updated list
        setSelectedGeofence(null); // Clear selected geofence
    }

    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 5000)
        }
    }, [toastData])
    
    // Helper function to render the appropriate content based on state
    const renderContent = () => {
        if (showAdd) {
            return (
                <AddGeofence
                    darkMode={darkMode}
                    tagData={tagData}
                    setShowAdd={setShowAdd}
                    selectedGeofence={selectedGeofence}
                    setSelectedGeofence={setSelectedGeofence}
                    refreshList={refreshList}
                    setIsAssetUpdated={setIsAssetUpdated as unknown as Dispatch<SetStateAction<boolean>>}
                />
            );
        }

        if (isLoading) {
            return (
                <LoadingSpinner darkMode={darkMode} />
            );
        }

        // Not adding, not loading: show list or empty state
        if (sortedGeofenceData.length > 0) {
            return (
                <div className="geofence" data-testid="geofence">
                    <div className="add-btn-container">
                        <Button
                            darkMode={darkMode}
                            label={t('addNew')}
                            size={'sm'}
                            variant={'primary'}
                            onClick={() => {
                                setSelectedGeofence(null); // Clear any selected geofence
                                setShowAdd(true)
                            }}
                            disabled={isGuest}
                        />
                    </div>
                    <div data-testid="geofence-card">
                        {sortedGeofenceData.map((data: SingleAssetGeofencesType) => {
                            return (
                                <Card
                                    darkMode={darkMode}
                                    editHandler={editGeofence}
                                    type={GEOFENCE}
                                    geofenceData={data}
                                    key={data.geofence_id}
                                    setLoader={setIsLoading}
                                    renderToastMessage={renderToast}
                                    setIsAssetUpdated={setIsAssetUpdated}
                                />
                            )
                        })}
                    </div>
                </div>
            );
        }

        // No geofences: show empty state
        return (
            <div className="no-geofence-centered">
                <p className="no-geofence-msg">{t("noConfiguredGeofences")}</p>
                <div className="no-geofence-add-btn">
                    <Button
                        label={t('addNew')}
                        size={'sm'}
                        variant={'primary'}
                        onClick={() => {
                            setSelectedGeofence(null); // Clear any selected geofence
                            setShowAdd(true)
                        }}
                    />
                </div>
            </div>
        );
    };

    return (
        <>
            {renderContent()}
            {showToast ? <div className="toast-container">
                <Notification 
                    title={toastData.variant == "success" ? "Success" : "Fail"}
                    textContent={toastData.message}
                    variant={toastData.variant}
                    onClose={()=>{setShowToast(false)}}
                />
            </div> : <></>}
        </>
    )
}
